import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Checkbox, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, Grid, InputLabel, Select, Snackbar, TextField, Typography } from "@mui/material";
import { Block as IconBlock, Search as IconSearch } from "@mui/icons-material";

import MantenimientoForm from "../../components/common/MantenimientoForm";
import DataGridBase from "../../components/common/DataGridBase";
import * as catalogoService from "../../services/catalogoService";
import * as personaService from "../../services/personaService";
import { getUsuario, addUpdateUsuario, addUpdateUsuarioRoles } from "../../services/usuarioService";
import { getsRol } from "../../services/seguridadService";
import { useMantenimiento } from "../../hooks/useMantenimiento";

// Alta y edicion de un usuario: codigo, persona, clave, estado y roles.
//
// La persona y los datos del comprobante (en las referencias de otras pantallas)
// se eligen en una ventana de busqueda, no escribiendo el codigo a mano.
const ESTADO_ACTIVO = 2;
const ESTADO_INACTIVO = 3;

const Edit = () => {
    const { isReadOnly } = useMantenimiento("usuario");
    const params = useParams();
    const navigate = useNavigate();

    const codigo = decodeURIComponent(params.id || "");

    const [entity, setEntity] = useState({
        codigo: "",
        idPersona: 0,
        nombres: "",
        numeroDocumento: "",
        clave: "",
        fechaVencimientoClave: null,
        idEstado: ESTADO_ACTIVO,
        claveCargada: false,
    });

    const [roles, setRoles] = useState([]);          // catalogo de roles
    const [rolesUsuario, setRolesUsuario] = useState([]);  // roles del usuario
    const [listEstado, setListEstado] = useState([]);

    const [nuevo, setNuevo] = useState(codigo == "" || codigo == "0");
    const [cargando, setCargando] = useState(true);

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    // Ventana de busqueda de personas.
    const [buscandoPersona, setBuscandoPersona] = useState(false);
    const [filtroPersona, setFiltroPersona] = useState({ nombres: "" });
    const [personas, setPersonas] = useState([]);

    const fetchEntity = useCallback(async () => {
        if (nuevo) {
            setCargando(false);
            return;
        }

        setCargando(true);

        const data = await getUsuario(codigo).catch((err) => { console.log("Err: ", err) });

        if (data) {
            setEntity({
                codigo: data.codigo,
                idPersona: data.idPersona,
                nombres: data.nombres || "",
                numeroDocumento: data.numeroDocumento || "",
                clave: "",
                claveCargada: data.claveCargada,
                fechaVencimientoClave: data.fechaVencimientoClave,
                idEstado: data.idEstado,
            });
            setRolesUsuario(data.roles || []);
        }

        setCargando(false);
    }, [codigo, nuevo])

    const fetchListRoles = useCallback(async () => {
        const data = await getsRol().catch((err) => { console.log("Err: ", err) });

        setRoles(data || []);
    }, [])

    const fetchListEstado = useCallback(async () => {
        const data = await catalogoService.getCatalogosByPadre(1)
            .catch((err) => { console.log("Err: ", err) });

        setListEstado(data || []);
    }, [])

    // Al elegir la persona se muestran sus nombres y documento en el formulario.
    const fetchPersona = useCallback(async () => {
        const data = await personaService.getsPersonasResumenViewByNumeroDocumentoYNombres(
            "", filtroPersona.nombres, "", 1)
            .catch((err) => { console.log("Err: ", err) });

        setPersonas(data || []);
    }, [filtroPersona])

    useEffect(() => {
        fetchEntity();
        fetchListRoles();
        fetchListEstado();
    }, [codigo])

    const seleccionarPersona = (persona) => {
        setEntity((prev) => ({
            ...prev,
            idPersona: persona.id,
            nombres: persona.nombres,
            numeroDocumento: persona.numeroDocumento,
        }));
        setBuscandoPersona(false);
    }
    const guardar = async () => {
        if (entity.codigo.trim() == "") {
            setPropsMessage({ severity: "error", message: "Indique el codigo del usuario." });
            setOpenMessage(true);
            return;
        }

        if (!nuevo && entity.clave != "" && entity.clave.length < 8) {
            setPropsMessage({ severity: "error", message: "La clave debe tener al menos 8 caracteres." });
            setOpenMessage(true);
            return;
        }

        const codigoUsuario = await addUpdateUsuario({
            codigo: String(entity.codigo).trim().toUpperCase(),
            idPersona: entity.idPersona || 0,
            clave: entity.clave == null ? "" : entity.clave,
            idEstado: entity.idEstado || ESTADO_ACTIVO,
        }).catch((err) => { console.log("Err: ", err) });

        if (!codigoUsuario) {
            setPropsMessage({ severity: "error", message: "No se pudo guardar el usuario." });
            setOpenMessage(true);
            return;
        }

        // Los roles se mandan todos juntos: los marcados conceden y los
        // desmarcados quedan inactivos.
        await addUpdateUsuarioRoles(codigoUsuario, roles.map((rol) => ({
            idRol: rol.id,
            idEstado: rolesUsuario.some(x => x.idRol == rol.id) ? ESTADO_ACTIVO : ESTADO_INACTIVO,
        }))).catch((err) => { console.log("Err: ", err) });

        setPropsMessage({ severity: "success", message: "Se guardaron los datos del usuario con exito." });
        setOpenMessage(true);

        if (nuevo) navigate("/security/user/edit/" + encodeURIComponent(codigoUsuario));
    }

    const alternarRol = (idRol) => {
        setRolesUsuario((prev) => {
            const existe = prev.some(x => x.idRol == idRol);

            if (existe) return prev.filter(x => x.idRol != idRol);

            return [...prev, { idRol: idRol, codigo: entity.codigo, idEstado: ESTADO_ACTIVO }];
        });
    }

    const columnasPersonas = [
        { field: 'nombres', headerName: 'Nombres', width: 220, sortable: false, headerAlign: 'left' },
        { field: 'numeroDocumento', headerName: 'Documento', width: 120, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'tipoDocumento', headerName: 'Tipo', width: 100, sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'seleccionar', headerName: '', width: 100, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => (
                <Box sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => seleccionarPersona(cellValues.row)}>
                    {"Seleccionar"}
                </Box>
            )
        },
    ];

    return (
        <div>
            <MantenimientoForm
                title={(nuevo ? "Nuevo" : "Editar") + " Usuario"}
                backTo={"/security/user"}
                onSave={guardar}
                disabled={isReadOnly}
            >
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <Grid container spacing={1} alignItems={"center"}>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            disabled={!nuevo}
                                            name="codigo"
                                            label="Codigo"
                                            value={entity.codigo}
                                            fullWidth
                                            onChange={(event) => setEntity({ ...entity, codigo: event.target.value })}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3} display={"flex"} alignItems={"flex-end"}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<IconSearch />}
                                            disabled={isReadOnly}
                                            onClick={() => { setBuscandoPersona(true); setPersonas([]); }}
                                        >
                                            {"Buscar persona"}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            disabled
                                            name="persona"
                                            label="Persona"
                                            value={entity.idPersona == 0 ? "" : entity.nombres}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            disabled
                                            name="numeroDocumento"
                                            label="Documento"
                                            value={entity.numeroDocumento}
                                            fullWidth
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={1} alignItems={"center"}>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            name="clave"
                                            label={entity.claveCargada ? "Clave nueva (opcional)" : "Clave"}
                                            type="password"
                                            value={entity.clave}
                                            fullWidth
                                            disabled={isReadOnly}
                                            onChange={(event) => setEntity({ ...entity, clave: event.target.value })}
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth>
                                            <InputLabel htmlFor="input-estadoedit">Estado</InputLabel>
                                            <Select
                                                native
                                                label="Estado"
                                                value={entity.idEstado}
                                                disabled={isReadOnly}
                                                onChange={(event) => setEntity({ ...entity, idEstado: parseInt(event.target.value) })}
                                                inputProps={{ name: 'idEstado', id: 'input-estadoedit' }}
                                                size="small"
                                            >
                                                {listEstado.map((row) => (
                                                    <option key={row.id} value={row.id}>{row.descripcion}</option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "0px 10px 10px 10px !important" }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {"Roles del usuario"}
                                </Typography>
                                <Grid container>
                                    {roles.map((rol) => (
                                        <Grid item xs={12} sm={4} key={rol.id}>
                                            <Checkbox
                                                checked={rolesUsuario.some(x => x.idRol == rol.id)}
                                                disabled={isReadOnly}
                                                onChange={() => alternarRol(rol.id)}
                                            />
                                            <Typography variant="body2" sx={{ display: 'inline' }}>
                                                {rol.nombre + " - " + (rol.descripcion || "")}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </MantenimientoForm>

            {/* Busqueda de la persona del usuario. */}
            <Dialog open={buscandoPersona} fullWidth maxWidth={"md"} onClose={() => setBuscandoPersona(false)}>
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Buscar Persona
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={1} alignItems={"center"} sx={{ paddingTop: "12px", paddingBottom: "12px" }}>
                        <Grid item xs={8} sm={4}>
                            <TextField
                                name="nombres"
                                label="Nombres"
                                value={filtroPersona.nombres}
                                fullWidth
                                onChange={(event) => setFiltroPersona({ ...filtroPersona, nombres: event.target.value })}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        fetchPersona();
                                    }
                                }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={4} sm={1} display={"flex"} alignItems={"center"}>
                            <Button variant="outlined" size="medium" fullWidth onClick={() => { fetchPersona() }}>
                                <IconSearch />
                            </Button>
                        </Grid>
                    </Grid>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                        <DataGridBase rows={personas} columns={columnasPersonas} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant={"outlined"} startIcon={<IconBlock />} onClick={() => setBuscandoPersona(false)}>
                        {"Cerrar"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(!openMessage)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(!openMessage)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

export default Edit;
